import { useCallback, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Formik, Form } from "formik";
import Drawer, { selectSelectedDrawer } from "../../../organisms/Drawer";
import { actions as dataModelActions } from "../../../../pages/DataModels/dataModels.reducers";
import { actions } from "../../../organisms/Drawer/drawers.reducers";
import {
  addNewRecordIntoTable,
  fetchCollections,
  fetchAllRecordsFromTable,
  selectDataModelsFilters,
  selectSelectedDataModelId,
  selectSelectedDataTable,
  selectSelectedRecords,
  updateRecordIntoTable,
  selectReferenceCollectionsData,
} from "../../../../pages/DataModels";
import { Alert } from "../../../molecules";
import {
  renderField,
  getInitialValues,
  getValidationSchema,
} from "../drawers.helpers";
import { getBase64FromFile } from "../../../../helpers";
import { INTERFACES, OPERATIONS } from "../drawers.constants";
import { BUTTON_TEXT, INDEX, EMPTY_ARRAY, ALERT_MESSAGES } from "../../../../constants";
import Spinner, { SPINNER_NAMES } from "../../Spinner";

const { FIRST } = INDEX;
const { SELECT, REPEATER_FIELDS, FILE, IMAGE, TOGGLE } = INTERFACES;
const { SUBMIT } = BUTTON_TEXT;
const { setSelectedDrawer } = actions;
const { resetSelectedProperty } = dataModelActions;
const { UPDATE_RECORD, ADD_RECORD } = SPINNER_NAMES;
const { EXTENSION_NOT_SUPPORTED } = ALERT_MESSAGES

const ManageRecordDrawer = () => {
  const dispatch = useDispatch();
  const selectedDrawer = useSelector(selectSelectedDrawer);
  const selectedDataModelId = useSelector(selectSelectedDataModelId);
  const selectedTable = useSelector(selectSelectedDataTable);
  const selectedRecords = useSelector(selectSelectedRecords);
  const dataModelsFilters = useSelector(selectDataModelsFilters);
  const referenceCollectionsData = useSelector(selectReferenceCollectionsData);

  const [columns, setColumns] = useState(EMPTY_ARRAY);
  const [initialValues, setInitialValues] = useState();
  const [validationSchema, setValidationSchema] = useState();
  const [primaryKey, setPrimaryKey] = useState();
  const [imageTypeFields, setImageTypeFields] = useState(EMPTY_ARRAY);
  const [isSpinnerActive, setIsSpinnerActive] = useState(false);
  const [alertData, setAlertData] = useState();

  useEffect(() => {
    if (selectedTable) {
      const primaryKeyField = selectedTable.schema.properties.find(
        (each) => each.meta.isKey
      );

      const fieldsWithImageType = selectedTable.schema.properties.reduce(
        (imgField, each) => {
          if (each.meta.interface === IMAGE || each.meta.interface === FILE) {
            const field = each.schema.key;
            return [...imgField, field];
          }
          return imgField;
        },
        []
      );
      setImageTypeFields(fieldsWithImageType);

      primaryKeyField && setPrimaryKey(primaryKeyField.schema.key);

      const validations = getValidationSchema(selectedTable.schema.properties);
      if (selectedRecords.length === 1) {
        setInitialValues(selectedRecords[FIRST]);
      } else {
        const initials = getInitialValues(selectedTable.schema.properties);
        setInitialValues(initials);
      }
      setValidationSchema(validations);
      setColumns(selectedTable.schema.properties);
    }
  }, [selectedTable, selectedRecords]);

  const handleFileUpload = async (e, setFieldValue, field) => {
    const file = e.target.files[FIRST];
    const fileExtension = file.name.split(".").pop();

    const isInvalidImageType =
      field.meta.interface === IMAGE &&
      !field.meta.selectedChoices.includes(`.${fileExtension},`);

    if (isInvalidImageType)
      setAlertData({ message: EXTENSION_NOT_SUPPORTED });
    else {
      const base64Url = await getBase64FromFile(file);
      setFieldValue(
        e.target.name,
        JSON.stringify({ name: file.name, base64Url })
      );
    }
  };

  const getFieldOptions = (field) => {
    const referencedCollection = referenceCollectionsData.find(
      ({ id }) => id === field.schema.referenceTableId
    );
    if (referencedCollection) {
      const referencedCollectionKeys = Array.from(
        new Set(
          referencedCollection.data.map(
            (data) => data[field.schema.referenceTableColumn || "id"]
          )
        )
      );
      return referencedCollectionKeys;
    } else return field.meta.options;
  };

  const getFilteredValues = (record) =>
    Object.entries(record).filter(
      ([key, value]) => (key === TOGGLE && value === false) || !!value
    );

  const handleOnSubmit = useCallback(
    (values, { resetForm }) => {
      delete values[primaryKey];
      delete values.created_at;
      const tableDataDetails = {
        tableName: selectedTable.refName,
        schema: { ...values },
      };

      Object.entries(values).reduce((formattedValues, [key, val]) => {
        if (imageTypeFields.includes(key))
          values = { ...values, [key]: val && JSON.parse(val) };
        return formattedValues;
      });

      dispatch(
        selectedRecords
          ? updateRecordIntoTable({
              dataModelId: selectedDataModelId,
              tableDataDetails: {
                selectedIds: selectedRecords.map((each) => each[primaryKey]),
                tableName: selectedTable.refName,
                operation: OPERATIONS.UPDATE,
                tableFields: Object.fromEntries(getFilteredValues(values)),
              },
            })
          : addNewRecordIntoTable({
              id: selectedDataModelId,
              tableDataDetails,
            })
      ).then((res) => {
        if (res.payload) {
          dispatch(
            fetchAllRecordsFromTable({
              id: selectedDataModelId,
              tableName: selectedTable.refName,
            })
          );
          dispatch(
            fetchCollections({
              id: selectedDataModelId,
              filters: dataModelsFilters,
            })
          );
          dispatch(setSelectedDrawer(null));
          dispatch(resetSelectedProperty());
          resetForm();
        }
      });
    },
    [
      dispatch,
      selectedDataModelId,
      selectedTable,
      selectedRecords,
      dataModelsFilters,
      primaryKey,
      imageTypeFields,
    ]
  );

  return (
    <Formik
      enableReinitialize
      initialValues={initialValues}
      validationSchema={!selectedRecords && validationSchema}
      onSubmit={handleOnSubmit}
    >
      {({
        values,
        errors,
        touched,
        handleChange,
        handleBlur,
        handleReset,
        setFieldValue,
        isValid,
        dirty,
      }) => {
        const handleClose = () => {
          handleReset();
          dispatch(setSelectedDrawer(null));
        };

        const footer = (
          <div className="p-6 border-t border-gray-700 flex align-center justify-end">
            <button
              className="btn btn-primary-light mr-1"
              type="button"
              onClick={() => handleClose()}
            >
              Cancel
            </button>
            <button
              className="btn btn-primary"
              type={SUBMIT}
              disabled={isSpinnerActive || !isValid || !dirty}
            >
              <Spinner
                name={selectedRecords ? UPDATE_RECORD : ADD_RECORD}
                setIsSpinnerActive={setIsSpinnerActive}
                persistSize
              >
                {SUBMIT}
              </Spinner>
            </button>
          </div>
        );
        return (
          <Form>
            <Drawer
              position="right"
              heading="Manage Record"
              handleClose={() => handleClose()}
              selectedDrawer={selectedDrawer}
              footer={footer}
            >
              <div className="flex-1 overflow-auto space-y-4 p-6 border-b border-gray-700 scrollbar">
                {alertData && (
                  <div className="my-2">
                    <Alert
                      message={alertData.message}
                      handleOnClose={setAlertData}
                    />
                  </div>
                )}
                {columns &&
                  values &&
                  columns.map(
                    (each) =>
                      !each.meta.hidden && (
                        <div key={each.schema.key}>
                          {renderField({
                            value: values[each.schema.key],
                            name: `${each.schema.key}`,
                            label: `${each.name}`,
                            errors,
                            touched,
                            handleBlur,
                            handleChange: async (e) => {
                              const shouldUploadFileOrImage =
                                each.meta.interface === FILE ||
                                each.meta.interface === IMAGE;
                              if (shouldUploadFileOrImage)
                                await handleFileUpload(e, setFieldValue, each);
                              else handleChange(e);
                            },
                            options: getFieldOptions(each),
                            interfaceType:
                              each.meta.interface === REPEATER_FIELDS
                                ? SELECT
                                : each.meta.interface,
                            shouldShowValidationMessage: true,
                            setFieldValue,
                            disabled: each.meta.readOnly,
                            selectedChoices: each.meta.selectedChoices,
                            type: each.schema.type,
                          })}
                        </div>
                      )
                  )}
              </div>
            </Drawer>
          </Form>
        );
      }}
    </Formik>
  );
};

export default ManageRecordDrawer;
